import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import { uiStore } from '../managers/globalState';

import '../../www/assets/css/geology_map_legend.css';

const DEFAULT_LEGEND_TITLE = 'الوحدات';

const formatLegendLabel = text => {
	const label = String(text ?? '').replace(/[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '').trim();
	// Keep numeric signs separate from range separators in the RTL legend.
	const number = '[+−-]?\\s*[0-9٠-٩]+(?:[.,٬٫][0-9٠-٩]+)*(?:\\s*[−-])?';
	const formatNumber = value => {
		const compact = value.replace(/\s+/g, '');
		const negative = /^[−-]|[−-]$/.test(compact);
		return `${negative ? '−' : ''}${compact.replace(/^[+−-]|[−-]$/g, '')}`;
	};
	const leadingNumber = '[+−-]?\\s*[0-9٠-٩]+(?:[.,٬٫][0-9٠-٩]+)*';
	const range = label.match(new RegExp(`^(${leadingNumber})\\s*[-–—]\\s*(${leadingNumber})$`))
		|| label.match(new RegExp(`^(${number})\\s*[-–—]\\s*(${number})$`));
	if (range) {
		return `${formatNumber(range[1])} – ${formatNumber(range[2])}`;
	}
	return new RegExp(`^${number}$`).test(label) ? formatNumber(label) : label;
};

const renderLegendLabel = text => {
	const label = formatLegendLabel(text);
	const range = label.split(' – ');
	if (range.length === 2) {
		return (
			<span className='leg-range' dir='ltr'>
				<span>{range[0]}</span>
				<span>–</span>
				<span>{range[1]}</span>
			</span>
		);
	}
	return <bdi dir={/^[\s+\-−]*[0-9٠-٩]/.test(label) ? 'ltr' : 'auto'}>{label}</bdi>;
};

const GeologyMapLegend = ({ current }) => {
	const [isHovered, setIsHovered] = useState(false);
	const [isPinned, setIsPinned] = useState(false);
	const [isDismissed, setIsDismissed] = useState(false);
	const wrapperRef = useRef(null);
	const location = useLocation();
	const { overlayType, controlsMenu, isDetailPanelOpen } = useSnapshot(uiStore.state);
	const legend = current?.legend;
	const legendImage = current?.legendImage;
	const legendAlt = current?.legendAlt || 'legend';
	const legendData = Array.isArray(legend?.data) ? legend.data : null;
	const legendTitle = legend?.title?.trim() || DEFAULT_LEGEND_TITLE;
	const rangeWidths = (legendData || []).reduce((widths, item) => {
		const range = formatLegendLabel(item.text).split(' – ');
		if (range.length === 2) {
			widths[0] = Math.max(widths[0], range[0].length);
			widths[1] = Math.max(widths[1], range[1].length);
		}
		return widths;
	}, [1, 1]);
	const isOpen = isHovered || isPinned;
	const closeLegend = useCallback(() => {
		setIsHovered(false);
		setIsPinned(false);
	}, []);
	const dismissLegend = useCallback(() => {
		setIsDismissed(true);
		closeLegend();
	}, [closeLegend]);
	const resetLegend = useCallback(() => {
		setIsDismissed(false);
		closeLegend();
	}, [closeLegend]);
	const isHeaderOrFooterTarget = useCallback(
		target => target instanceof Element && Boolean(target.closest('#top-panel, #bottom-panel')),
		[]
	);

	useEffect(() => {
		resetLegend();
	}, [resetLegend, current?.id, legendImage, legendData?.length]);

	useEffect(() => {
		resetLegend();
	}, [resetLegend, location.pathname, location.search, location.hash]);

	useEffect(() => {
		if (overlayType || controlsMenu || isDetailPanelOpen) {
			dismissLegend();
		}
	}, [dismissLegend, overlayType, controlsMenu, isDetailPanelOpen]);

	useEffect(() => {
		const handlePointerDown = event => {
			if (!wrapperRef.current || wrapperRef.current.contains(event.target)) {
				return;
			}

			if (isHeaderOrFooterTarget(event.target)) {
				dismissLegend();
			} else {
				closeLegend();
			}
		};

		const handleDocumentClick = event => {
			if (!wrapperRef.current || wrapperRef.current.contains(event.target)) {
				return;
			}

			if (isHeaderOrFooterTarget(event.target)) {
				dismissLegend();
			} else {
				closeLegend();
			}
		};

		const handleEscape = event => {
			if (event.key === 'Escape') {
				closeLegend();
			}
		};

		const handleFocusIn = event => {
			if (!wrapperRef.current || wrapperRef.current.contains(event.target)) {
				return;
			}

			if (isHeaderOrFooterTarget(event.target)) {
				dismissLegend();
			} else {
				closeLegend();
			}
		};

		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('click', handleDocumentClick, true);
		document.addEventListener('focusin', handleFocusIn, true);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('click', handleDocumentClick, true);
			document.removeEventListener('focusin', handleFocusIn, true);
			document.removeEventListener('keydown', handleEscape);
		};
	}, [closeLegend, dismissLegend, isHeaderOrFooterTarget]);

	if (isDismissed || (!legendImage && !legendData?.length)) {
		return null;
	}

	return (
		<div className='legend-popup'>
			<div
				ref={wrapperRef}
				className='legend-popup-anchor'
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
			>
				<button
					type='button'
					className={`legend-trigger ${legendImage ? 'image' : 'table'}`}
					aria-expanded={isOpen}
					aria-label='Show legend'
					onClick={() => setIsPinned(prev => !prev)}
				>
					{legendImage
						? <img className='legend-trigger-image' src={legendImage} alt={legendAlt} />
						: (
							<div className='legend-trigger-preview'>
								<p className='legend-trigger-title'>{legendTitle}</p>
								<div className='legend-trigger-swatches'>
									{legendData.slice(0, 4).map((item, index) => (
										<span
											key={index}
											className='legend-trigger-swatch'
											style={{ background: item.color }}
										/>
									))}
								</div>
							</div>
						)}
				</button>
				{isOpen && (
					<div className={`legend-popup-card ${legendImage ? 'image' : 'table'}`}>
						{legendImage
							? <img className='geo-legend-image' src={legendImage} alt={legendAlt} />
							: (
								<div className='geo-legend'>
									<p className='geo-legend-title'>{legendTitle}</p>
									<div className='leg-table-scroll' tabIndex={0} role='region' aria-label={legendTitle}>
										<table className='leg-table' style={{ '--legend-range-start-width': `${rangeWidths[0]}ch`, '--legend-range-end-width': `${rangeWidths[1]}ch` }}>
											<tbody>
												{legendData.map((item, index) => (
													<tr key={index}>
														<td className='leg-td leg-swatch' style={{ background: item.color }}>{item.letter ?? ''}</td>
														<td className='leg-td leg-label'>
															{renderLegendLabel(item.text)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							)}
					</div>
				)}
			</div>
		</div>
	);
};

export default GeologyMapLegend;
